import { useState, useEffect } from 'react';
import { useClub } from '../App';
import { toast } from 'sonner';
import api from '../utils/api';
import { X, Link2, Copy, Trash2, Plus, Clock, Users, Mail, Send } from 'lucide-react';

export default function InviteLinksModal({ onClose }) {
  const { selectedClub } = useClub();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState('player');

  const fetchLinks = () => {
    if (!selectedClub) return;
    api.get(`/clubs/${selectedClub.id}/invite-links`)
      .then(r => setLinks(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLinks(); }, [selectedClub]);

  const createLink = async () => {
    setCreating(true);
    try {
      const res = await api.post(`/clubs/${selectedClub.id}/invite-link`, { role: newRole });
      toast.success('Invite link created!');
      fetchLinks();
      // Auto-copy to clipboard
      const url = `${window.location.origin}/join/${res.data.code}`;
      navigator.clipboard.writeText(url).then(() => toast.success('Link copied to clipboard!'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/join/${code}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied!'));
  };

  const revokeLink = async (linkId) => {
    try {
      await api.delete(`/clubs/${selectedClub.id}/invite-links/${linkId}`);
      toast.success('Link revoked');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to revoke link');
    }
  };

  const [emailTarget, setEmailTarget] = useState(null); // code of link being emailed
  const [emailAddr, setEmailAddr] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const sendByEmail = async (code) => {
    if (!emailAddr.trim()) { toast.error('Enter an email address'); return; }
    setEmailSending(true);
    try {
      await api.post(`/clubs/${selectedClub.id}/send-invite-email`, {
        link_code: code,
        email: emailAddr.trim(),
      });
      toast.success(`Invite sent to ${emailAddr.trim()}`);
      setEmailTarget(null);
      setEmailAddr('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg" data-testid="invite-links-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Invite Links
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Share a link for players to join {selectedClub?.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-invite-modal">
            <X size={18} />
          </button>
        </div>

        {/* Create new link */}
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <select
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="invite-role-select"
            >
              <option value="player">Player</option>
              <option value="contributor">Contributor</option>
              <option value="admin">Admin</option>
            </select>
            <button
              onClick={createLink}
              disabled={creating}
              className="flex items-center gap-1.5 h-9 px-4 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-sm font-medium rounded-lg transition-all"
              data-testid="create-invite-link-btn"
            >
              <Plus size={14} />
              {creating ? 'Creating...' : 'Create Link'}
            </button>
          </div>
        </div>

        {/* Links list */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading...</div>
          ) : links.length === 0 ? (
            <div className="py-8 text-center">
              <Link2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No invite links yet</p>
              <p className="text-xs text-slate-400 mt-1">Create one to share with players</p>
            </div>
          ) : (
            links.map(link => (
              <div key={link.id} className="px-6 py-3 border-b border-slate-50" data-testid={`invite-link-${link.code}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Link2 size={14} className="text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-slate-600 truncate">{link.code}</code>
                      <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded capitalize">{link.role}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Users size={10} /> {link.use_count} used</span>
                      {link.expires_at && <span className="flex items-center gap-1"><Clock size={10} /> Expires {new Date(link.expires_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => copyLink(link.code)}
                      className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                      title="Copy link"
                      data-testid={`copy-link-${link.code}`}
                    >
                      <Copy size={14} />
                    </button>
                    <button
                      onClick={() => { setEmailTarget(emailTarget === link.code ? null : link.code); setEmailAddr(''); }}
                      className={`p-1.5 rounded-lg transition-all ${emailTarget === link.code ? 'text-green-600 bg-green-50' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`}
                      title="Send by email"
                      data-testid={`email-link-${link.code}`}
                    >
                      <Mail size={14} />
                    </button>
                    <button
                      onClick={() => revokeLink(link.id)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Revoke link"
                      data-testid={`revoke-link-${link.code}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {emailTarget === link.code && (
                  <div className="mt-2 ml-11 flex items-center gap-2" data-testid={`email-form-${link.code}`}>
                    <input
                      type="email"
                      value={emailAddr}
                      onChange={e => setEmailAddr(e.target.value)}
                      placeholder="recipient@email.com"
                      className="flex-1 h-8 rounded-lg border border-slate-200 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                      data-testid={`email-input-${link.code}`}
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendByEmail(link.code); } }}
                    />
                    <button
                      onClick={() => sendByEmail(link.code)}
                      disabled={emailSending}
                      className="flex items-center gap-1 h-8 px-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-xs font-medium rounded-lg transition-all"
                      data-testid={`send-email-btn-${link.code}`}
                    >
                      <Send size={12} />
                      {emailSending ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-400">
          Links expire after 7 days. Revoked links stop working immediately.
        </div>
      </div>
    </div>
  );
}
